/**
 * SAX Editor Store
 *
 * FOR: pricing-portal-laravel/resources/js/stores/saxEditor.js
 *
 * Manages state for SAX Editor integrated into equipment documentation
 */

import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { api } from '@/services/api'; // Assuming this exists in pricing-portal

export const useSaxEditorStore = defineStore('saxEditor', () => {
  // ====================================================================
  // STATE
  // ====================================================================

  const currentSaxData = ref(null);
  const selectedComponents = ref([]);
  const selectedLinks = ref([]);
  const clipboardData = ref(null);
  const isLoading = ref(false);
  const error = ref(null);

  // History for undo/redo
  const history = ref([]);
  const historyIndex = ref(-1);
  const maxHistorySize = ref(50);

  // Canvas state
  const canvasSettings = ref({
    zoom: 1.0,
    pan_x: 0,
    pan_y: 0,
    grid_size: 20,
    snap_to_grid: true,
  });

  // Metadata
  const currentEquipmentId = ref(null);
  const lastSaved = ref(null);
  const isDirty = ref(false);

  // ====================================================================
  // COMPUTED
  // ====================================================================

  const canUndo = computed(() => historyIndex.value > 0);
  const canRedo = computed(() => historyIndex.value < history.value.length - 1);

  const componentCount = computed(() => {
    return currentSaxData.value?.components?.length || 0;
  });

  const linkCount = computed(() => {
    return currentSaxData.value?.links?.length || 0;
  });

  const hasSaxData = computed(() => {
    return currentSaxData.value && componentCount.value > 0;
  });

  const selectedComponent = computed(() => {
    if (selectedComponents.value.length === 1) {
      return currentSaxData.value?.components.find(
        (c) => c.id === selectedComponents.value[0],
      );
    }
    return null;
  });

  // ====================================================================
  // ACTIONS - LOADING & SAVING
  // ====================================================================

  /**
   * Load SAX data for equipment
   */
  async function loadSaxDataForEquipment(equipmentId, siteId = null) {
    isLoading.value = true;
    error.value = null;
    currentEquipmentId.value = equipmentId;

    try {
      // Use V2 API if siteId provided, otherwise V1
      const endpoint = siteId
        ? `/v2/sites/${siteId}/equipment/${equipmentId}/documentation/sax`
        : `/equipment/${equipmentId}/documentation/sax`;

      const data = await api.get(endpoint);

      if (data.has_sax_program) {
        currentSaxData.value = data.sax_data;
        canvasSettings.value = data.sax_data.canvas_settings || canvasSettings.value;
        lastSaved.value = new Date();
        isDirty.value = false;

        // Initialize history
        history.value = [JSON.parse(JSON.stringify(currentSaxData.value))];
        historyIndex.value = 0;
      } else {
        // Initialize empty SAX data
        initializeEmptySaxData();
      }

      return currentSaxData.value;
    } catch (err) {
      error.value = err.data?.message || err.message || 'Failed to load SAX data';
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Save SAX data
   */
  async function saveSaxData(equipmentId, createVersion = false, versionSummary = null, siteId = null) {
    isLoading.value = true;
    error.value = null;

    try {
      // Ensure SAX data structure exists
      if (!currentSaxData.value) {
        initializeEmptySaxData();
      }

      // Ensure required fields exist
      if (!currentSaxData.value.components) {
        currentSaxData.value.components = [];
      }
      if (!currentSaxData.value.links) {
        currentSaxData.value.links = [];
      }

      const endpoint = siteId
        ? `/v2/sites/${siteId}/equipment/${equipmentId}/documentation/sax`
        : `/equipment/${equipmentId}/documentation/sax`;

      const data = await api.put(endpoint, {
        programming_sax_data: currentSaxData.value,
        canvas_settings: canvasSettings.value,
        create_version: createVersion,
        version_summary: versionSummary,
      });

      currentSaxData.value = data.sax_data;
      lastSaved.value = new Date();
      isDirty.value = false;

      return data;
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to save SAX data';
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Auto-save (debounced externally)
   */
  async function autoSave(equipmentId, siteId = null) {
    if (!isDirty.value) {return;}

    try {
      await saveSaxData(equipmentId, false, 'Auto-save', siteId);
    } catch (err) {
      console.warn('Auto-save failed:', err);
      // error.value is already set by saveSaxData
    }
  }

  /**
   * Initialize empty SAX data structure
   */
  function initializeEmptySaxData() {
    currentSaxData.value = {
      format_version: '1.0',
      metadata: {
        created_at: new Date().toISOString(),
      },
      kits: [],
      canvas_settings: { ...canvasSettings.value },
      components: [],
      links: [],
    };

    history.value = [JSON.parse(JSON.stringify(currentSaxData.value))];
    historyIndex.value = 0;
    isDirty.value = false;
  }

  // ====================================================================
  // ACTIONS - IMPORT & EXPORT
  // ====================================================================

  /**
   * Import SAX file
   */
  async function importSaxFile(equipmentId, file, siteId = null) {
    isLoading.value = true;
    error.value = null;

    const formData = new FormData();
    formData.append('sax_file', file);
    formData.append('create_version', 'true');

    try {
      const endpoint = siteId
        ? `/v2/sites/${siteId}/equipment/${equipmentId}/documentation/sax/import`
        : `/equipment/${equipmentId}/documentation/sax/import`;

      const data = await api.post(endpoint, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      currentSaxData.value = data.sax_data;
      lastSaved.value = new Date();
      isDirty.value = false;

      // Reset history
      history.value = [JSON.parse(JSON.stringify(currentSaxData.value))];
      historyIndex.value = 0;

      return data;
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to import SAX file';
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Export SAX file
   */
  async function exportSaxFile(equipmentId, siteId = null) {
    isLoading.value = true;
    error.value = null;

    try {
      const endpoint = siteId
        ? `/v2/sites/${siteId}/equipment/${equipmentId}/documentation/sax/export`
        : `/equipment/${equipmentId}/documentation/sax/export`;

      const data = await api.get(endpoint, {
        responseType: 'blob',
      });

      return data; // Blob for download
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to export SAX file';
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  // ====================================================================
  // ACTIONS - UNDO/REDO
  // ====================================================================

  /**
   * Add current state to history
   */
  function addToHistory(saxData = null) {
    const dataToSave = saxData || currentSaxData.value;

    // Remove future history if we've undone
    if (historyIndex.value < history.value.length - 1) {
      history.value = history.value.slice(0, historyIndex.value + 1);
    }

    // Add new state
    history.value.push(JSON.parse(JSON.stringify(dataToSave)));
    historyIndex.value = history.value.length - 1;

    // Limit history size
    if (history.value.length > maxHistorySize.value) {
      history.value.shift();
      historyIndex.value--;
    }

    isDirty.value = true;
  }

  /**
   * Undo last change
   */
  function undo() {
    if (canUndo.value) {
      historyIndex.value--;
      currentSaxData.value = JSON.parse(JSON.stringify(history.value[historyIndex.value]));
      isDirty.value = true;
    }
  }

  /**
   * Redo last undone change
   */
  function redo() {
    if (canRedo.value) {
      historyIndex.value++;
      currentSaxData.value = JSON.parse(JSON.stringify(history.value[historyIndex.value]));
      isDirty.value = true;
    }
  }

  // ====================================================================
  // ACTIONS - COMPONENTS
  // ====================================================================

  /**
   * Add component to canvas
   */
  function addComponent(component) {
    if (!currentSaxData.value) {
      initializeEmptySaxData();
    }

    // Ensure required fields exist
    if (!currentSaxData.value.components) {
      currentSaxData.value.components = [];
    }
    if (!currentSaxData.value.links) {
      currentSaxData.value.links = [];
    }

    currentSaxData.value.components.push(component);
    addToHistory();
  }

  /**
   * Update component
   */
  function updateComponent(componentId, updates) {
    if (!currentSaxData.value) {return;}

    const component = currentSaxData.value.components.find((c) => c.id === componentId);
    if (component) {
      Object.assign(component, updates);
      addToHistory();
    }
  }

  /**
   * Delete component
   */
  function deleteComponent(componentId) {
    if (!currentSaxData.value) {return;}

    // Remove component
    currentSaxData.value.components = currentSaxData.value.components.filter(
      (c) => c.id !== componentId,
    );

    // Remove associated links
    currentSaxData.value.links = currentSaxData.value.links.filter(
      (l) => l.from_component !== componentId && l.to_component !== componentId,
    );

    // Remove from selection
    selectedComponents.value = selectedComponents.value.filter((id) => id !== componentId);

    addToHistory();
  }

  /**
   * Delete multiple components
   */
  function deleteComponents(componentIds) {
    componentIds.forEach((id) => deleteComponent(id));
  }

  /**
   * Move component
   */
  function moveComponent(componentId, newPosition) {
    updateComponent(componentId, { position: newPosition });
  }

  /**
   * Get component by ID
   */
  function getComponent(componentId) {
    return currentSaxData.value?.components.find((c) => c.id === componentId);
  }

  // ====================================================================
  // ACTIONS - LINKS
  // ====================================================================

  /**
   * Add link between components
   */
  function addLink(link) {
    if (!currentSaxData.value) {return;}

    // Check if link already exists
    const exists = currentSaxData.value.links.some(
      (l) =>
        l.from_component === link.from_component &&
        l.from_slot === link.from_slot &&
        l.to_component === link.to_component &&
        l.to_slot === link.to_slot,
    );

    if (!exists) {
      currentSaxData.value.links.push(link);
      addToHistory();
    }
  }

  /**
   * Delete link
   */
  function deleteLink(linkId) {
    if (!currentSaxData.value) {return;}

    currentSaxData.value.links = currentSaxData.value.links.filter((l) => l.id !== linkId);
    selectedLinks.value = selectedLinks.value.filter((id) => id !== linkId);

    addToHistory();
  }

  /**
   * Get links for component
   */
  function getLinksForComponent(componentId) {
    if (!currentSaxData.value) {return [];}

    return currentSaxData.value.links.filter(
      (l) => l.from_component === componentId || l.to_component === componentId,
    );
  }

  // ====================================================================
  // ACTIONS - SELECTION
  // ====================================================================

  /**
   * Select component
   */
  function selectComponent(componentId, addToSelection = false) {
    if (addToSelection) {
      if (!selectedComponents.value.includes(componentId)) {
        selectedComponents.value.push(componentId);
      }
    } else {
      selectedComponents.value = [componentId];
    }
  }

  /**
   * Deselect component
   */
  function deselectComponent(componentId) {
    selectedComponents.value = selectedComponents.value.filter((id) => id !== componentId);
  }

  /**
   * Clear selection
   */
  function clearSelection() {
    selectedComponents.value = [];
    selectedLinks.value = [];
  }

  /**
   * Select all components
   */
  function selectAll() {
    selectedComponents.value = currentSaxData.value?.components.map((c) => c.id) || [];
  }

  // ====================================================================
  // ACTIONS - CLIPBOARD
  // ====================================================================

  /**
   * Copy selected components
   */
  function copySelection() {
    if (selectedComponents.value.length === 0) {return;}

    const components = selectedComponents.value.map((id) => getComponent(id)).filter(Boolean);

    // Copy links between selected components
    const links = currentSaxData.value?.links.filter(
      (l) =>
        selectedComponents.value.includes(l.from_component) &&
        selectedComponents.value.includes(l.to_component),
    ) || [];

    clipboardData.value = {
      components: JSON.parse(JSON.stringify(components)),
      links: JSON.parse(JSON.stringify(links)),
    };
  }

  /**
   * Paste from clipboard
   */
  function paste(position = { x: 50, y: 50 }) {
    if (!clipboardData.value) {return;}

    const idMap = {};
    const newComponents = [];

    // Create new components with new IDs
    clipboardData.value.components.forEach((component, index) => {
      const newId = `comp_${Date.now()}_${index}`;
      idMap[component.id] = newId;

      const newComponent = {
        ...component,
        id: newId,
        position: {
          x: component.position.x + position.x,
          y: component.position.y + position.y,
        },
      };

      currentSaxData.value.components.push(newComponent);
      newComponents.push(newId);
    });

    // Create new links with updated component IDs
    clipboardData.value.links.forEach((link) => {
      const newLink = {
        ...link,
        id: `link_${Date.now()}_${Math.random()}`,
        from_component: idMap[link.from_component],
        to_component: idMap[link.to_component],
      };

      currentSaxData.value.links.push(newLink);
    });

    // Select newly pasted components
    selectedComponents.value = newComponents;

    addToHistory();
  }

  // ====================================================================
  // ACTIONS - CANVAS
  // ====================================================================

  /**
   * Update canvas settings
   */
  function updateCanvasSettings(settings) {
    Object.assign(canvasSettings.value, settings);
    isDirty.value = true;
  }

  /**
   * Reset canvas view
   */
  function resetCanvasView() {
    canvasSettings.value.zoom = 1.0;
    canvasSettings.value.pan_x = 0;
    canvasSettings.value.pan_y = 0;
  }

  // ====================================================================
  // ACTIONS - VALIDATION
  // ====================================================================

  /**
   * Validate SAX program
   */
  async function validateProgram(equipmentId, siteId = null) {
    isLoading.value = true;
    error.value = null;

    try {
      const endpoint = siteId
        ? `/v2/sites/${siteId}/equipment/${equipmentId}/documentation/sax/validate`
        : `/equipment/${equipmentId}/documentation/sax/validate`;

      const data = await api.post(endpoint);
      return data;
    } catch (err) {
      error.value = err.response?.data?.message || 'Validation failed';
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  // ====================================================================
  // FOLDER VIRTUAL SLOTS
  // ====================================================================

  /**
   * Compute virtual input/output slots for a folder based on external links
   * Virtual slots appear when links cross the folder boundary
   *
   * @param {string} folderId - The folder component ID
   * @returns {{inputs: Array, outputs: Array}}
   */
  function computeFolderVirtualSlots(folderId) {
    const folder = getComponent(folderId);
    if (!folder || folder.type !== 'sys::Folder') {
      return { inputs: [], outputs: [] };
    }

    const children = currentSaxData.value?.components?.filter((c) => c.parent_id === folderId) || [];
    const childIds = new Set(children.map((c) => c.id));

    const virtualInputs = new Map();   // Map<slotKey, slotData>
    const virtualOutputs = new Map();

    currentSaxData.value?.links?.forEach((link) => {
      // Input: External component → Child component (link coming INTO folder)
      if (childIds.has(link.to_component) && !childIds.has(link.from_component)) {
        const child = children.find((c) => c.id === link.to_component);
        const childSlot = child?.input_slots?.find((s) => s.name === link.to_slot);

        const key = `${child.name}_${link.to_slot}`;
        virtualInputs.set(key, {
          name: key,
          displayName: `${child.name}.${link.to_slot}`,
          type: childSlot?.type || 'any',
          target_component: link.to_component,
          target_slot: link.to_slot,
          virtual: true,
        });
      }

      // Output: Child component → External component (link going OUT of folder)
      if (childIds.has(link.from_component) && !childIds.has(link.to_component)) {
        const child = children.find((c) => c.id === link.from_component);
        const childSlot = child?.output_slots?.find((s) => s.name === link.from_slot);

        const key = `${child.name}_${link.from_slot}`;
        virtualOutputs.set(key, {
          name: key,
          displayName: `${child.name}.${link.from_slot}`,
          type: childSlot?.type || 'any',
          source_component: link.from_component,
          source_slot: link.from_slot,
          virtual: true,
        });
      }
    });

    return {
      inputs: Array.from(virtualInputs.values()),
      outputs: Array.from(virtualOutputs.values()),
    };
  }

  /**
   * Get all components inside a folder (filtered by type compatibility if needed)
   *
   * @param {string} folderId - The folder component ID
   * @param {string} slotType - Optional: filter by compatible slot type ('input' or 'output')
   * @param {string} dataType - Optional: filter by data type (e.g., 'float', 'int', 'bool')
   * @returns {Array} Array of components inside the folder
   */
  function getComponentsInFolder(folderId, slotType = null, dataType = null) {
    const components = currentSaxData.value?.components?.filter((c) => c.parent_id === folderId) || [];

    if (!slotType && !dataType) {
      return components;
    }

    // Filter by slot availability and type compatibility
    return components.filter((comp) => {
      if (slotType === 'input') {
        const hasCompatibleSlot = comp.input_slots?.some((slot) =>
          !dataType || slot.type === dataType || slot.type === 'any' || dataType === 'any',
        );
        return hasCompatibleSlot;
      }

      if (slotType === 'output') {
        const hasCompatibleSlot = comp.output_slots?.some((slot) =>
          !dataType || slot.type === dataType || slot.type === 'any' || dataType === 'any',
        );
        return hasCompatibleSlot;
      }

      return true;
    });
  }

  /**
   * Get links that connect to a specific component and slot
   *
   * @param {string} componentId - Component ID
   * @param {string} slotName - Slot name (optional)
   * @returns {Array} Array of links
   */
  function getLinksToComponent(componentId, slotName = null) {
    return currentSaxData.value?.links?.filter((link) => {
      const matchesComponent = link.to_component === componentId;
      const matchesSlot = !slotName || link.to_slot === slotName;
      return matchesComponent && matchesSlot;
    }) || [];
  }

  /**
   * Get links that originate from a specific component and slot
   *
   * @param {string} componentId - Component ID
   * @param {string} slotName - Slot name (optional)
   * @returns {Array} Array of links
   */
  function getLinksFromComponent(componentId, slotName = null) {
    return currentSaxData.value?.links?.filter((link) => {
      const matchesComponent = link.from_component === componentId;
      const matchesSlot = !slotName || link.from_slot === slotName;
      return matchesComponent && matchesSlot;
    }) || [];
  }

  // ====================================================================
  // RESET
  // ====================================================================

  /**
   * Reset store to initial state
   */
  function $reset() {
    currentSaxData.value = null;
    selectedComponents.value = [];
    selectedLinks.value = [];
    clipboardData.value = null;
    isLoading.value = false;
    error.value = null;
    history.value = [];
    historyIndex.value = -1;
    currentEquipmentId.value = null;
    lastSaved.value = null;
    isDirty.value = false;
    canvasSettings.value = {
      zoom: 1.0,
      pan_x: 0,
      pan_y: 0,
      grid_size: 20,
      snap_to_grid: true,
    };
  }

  // ====================================================================
  // RETURN PUBLIC API
  // ====================================================================

  return {
    // State
    currentSaxData,
    selectedComponents,
    selectedLinks,
    clipboardData,
    isLoading,
    error,
    canvasSettings,
    currentEquipmentId,
    lastSaved,
    isDirty,

    // Computed
    canUndo,
    canRedo,
    componentCount,
    linkCount,
    hasSaxData,
    selectedComponent,

    // Actions - Loading & Saving
    loadSaxDataForEquipment,
    saveSaxData,
    autoSave,
    initializeEmptySaxData,

    // Actions - Import & Export
    importSaxFile,
    exportSaxFile,

    // Actions - Undo/Redo
    undo,
    redo,
    addToHistory,

    // Actions - Components
    addComponent,
    updateComponent,
    deleteComponent,
    deleteComponents,
    moveComponent,
    getComponent,

    // Actions - Links
    addLink,
    deleteLink,
    getLinksForComponent,
    getLinksToComponent,
    getLinksFromComponent,

    // Actions - Folder Virtual Slots
    computeFolderVirtualSlots,
    getComponentsInFolder,

    // Actions - Selection
    selectComponent,
    deselectComponent,
    clearSelection,
    selectAll,

    // Actions - Clipboard
    copySelection,
    paste,

    // Actions - Canvas
    updateCanvasSettings,
    resetCanvasView,

    // Actions - Validation
    validateProgram,

    // Reset
    $reset,
  };
});
